import { useState } from 'react';
import { useCashControls } from '../../hooks/useCashControls';
import { formatCurrency } from '../../lib/cashValidation';
import { computeExcessCashAfterReserve, computeTotalLiquidity } from '../../lib/cashCalculations';
import type { CashControlsFormState } from '../../types/index';

interface CashControlsSectionProps {
  accountSlug: string;
}

function formatTimestamp(updatedAt: string | null): string | null {
  if (!updatedAt) return null;
  const date = new Date(updatedAt);
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const NUMERIC_FIELDS: { key: keyof Omit<CashControlsFormState, 'notes'>; label: string }[] = [
  { key: 'accountValue', label: 'Account Value' },
  { key: 'settledAvailableCash', label: 'Settled Available Cash' },
  { key: 'nonMarginBuyingPower', label: 'Non-Margin Buying Power' },
  { key: 'marginBuyingPower', label: 'Margin Buying Power' },
  { key: 'cashReservedForOptions', label: 'Cash Reserved for Options' },
  { key: 'targetCashReserve', label: 'Target Cash Reserve' },
];

export function CashControlsSection({ accountSlug }: CashControlsSectionProps) {
  const {
    data,
    formState,
    errors,
    calculatedValue,
    isLoading,
    isSaving,
    isExpanded,
    isDirty,
    loadError,
    saveError,
    saveSuccess,
    setField,
    save,
    cancel,
    toggleExpanded,
    retry,
  } = useCashControls(accountSlug);

  const hasErrors = Object.keys(errors).length > 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="mb-6 bg-gray-800 rounded-lg p-4">
        <p className="text-gray-400 text-sm">Loading...</p>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="mb-6 bg-gray-800 rounded-lg p-4">
        <p className="text-red-400 text-sm mb-2">{loadError}</p>
        <button
          onClick={retry}
          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-500"
        >
          Retry
        </button>
      </div>
    );
  }

  const updatedLabel = formatTimestamp(data?.updatedAt ?? null);

  return (
    <div className="mb-6 bg-gray-800 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between cursor-pointer" onClick={toggleExpanded}>
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm">{isExpanded ? '▼' : '▶'}</span>
          <h2 className="text-sm font-semibold text-white">Account Cash & Buying Power</h2>
        </div>
        {updatedLabel && (
          <span className="text-xs text-gray-500">Last updated: {updatedLabel}</span>
        )}
      </div>

      {/* Collapsed view — summary row */}
      {!isExpanded && (
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <SummaryItem label="Account Value" value={data?.accountValue ?? 0} />
          <SummaryItem label="Settled Cash" value={data?.settledAvailableCash ?? 0} />
          <SummaryItem label="Margin BP" value={data?.marginBuyingPower ?? 0} />
          <SummaryItem label="Reserved" value={data?.cashReservedForOptions ?? 0} />
          <SummaryItem label="Available" value={data?.cashAvailableForNewTrades ?? calculatedValue} />
        </div>
      )}

      {/* Expanded view — form */}
      {isExpanded && (
        <div className="mt-4">
          {/* Save error banner */}
          {saveError && (
            <div className="mb-3 p-2 rounded bg-red-900/30 border border-red-700">
              <p className="text-red-400 text-sm">{saveError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {NUMERIC_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <label className="block text-xs text-gray-400 mb-1">{label}</label>
                <input
                  type="text"
                  value={formState[key]}
                  onChange={(e) => setField(key, e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                {errors[key] && (
                  <p className="text-red-400 text-sm mt-1">{errors[key]}</p>
                )}
              </div>
            ))}

            {/* Notes — full width */}
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-400 mb-1">Notes</label>
              <textarea
                value={formState.notes}
                onChange={(e) => setField('notes', e.target.value)}
                maxLength={500}
                rows={3}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
              />
              {errors.notes && (
                <p className="text-red-400 text-sm mt-1">{errors.notes}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">{formState.notes.length}/500</p>
            </div>
          </div>

          {/* Calculated field */}
          <div className="mt-4">
            <label className="block text-xs text-gray-400 mb-1">Cash Available for New Trades (= Non-Margin Buying Power)</label>
            <div className="bg-gray-700 p-2 rounded font-semibold text-green-400 text-sm">
              ${formatCurrency(calculatedValue)}
            </div>
          </div>

          {/* Additional calculated metrics */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-gray-700/60 p-2 rounded">
              <label className="block text-xs text-gray-500 mb-0.5">Excess After Reserve</label>
              <p className="text-sm font-mono text-gray-200">
                ${formatCurrency(computeExcessCashAfterReserve(
                  parseFloat(formState.nonMarginBuyingPower) || 0,
                  parseFloat(formState.targetCashReserve) || 0
                ))}
              </p>
            </div>
            <div className="bg-gray-700/60 p-2 rounded">
              <label className="block text-xs text-gray-500 mb-0.5">Margin Capacity</label>
              <p className="text-sm font-mono text-gray-200">
                ${formatCurrency(parseFloat(formState.marginBuyingPower) || 0)}
              </p>
            </div>
            <div className="bg-gray-700/60 p-2 rounded">
              <label className="block text-xs text-gray-500 mb-0.5">Total Liquidity (Cash + Margin)</label>
              <p className="text-sm font-mono text-gray-200">
                ${formatCurrency(computeTotalLiquidity(
                  parseFloat(formState.nonMarginBuyingPower) || 0,
                  parseFloat(formState.marginBuyingPower) || 0
                ))}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={save}
              disabled={!isDirty || hasErrors || isSaving}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={cancel}
              disabled={!isDirty}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            {saveSuccess && (
              <span className="text-green-400 text-sm">✓ Saved</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryItem({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <span className="text-gray-500 text-xs">{label}</span>
      <p className="text-white text-sm font-medium">${formatCurrency(value)}</p>
    </div>
  );
}
